import axios from 'axios';

const GOOGLE_TILES_API_BASE = 'https://tile.googleapis.com/v1';
const SESSION_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

export type GoogleMapType = 'roadmap' | 'satellite';

interface CreateSessionRequest {
  mapType: GoogleMapType;
  language: string;
  region: string;
}

interface SessionResponse {
  session: string;
  expiry: string;
  tileWidth: number;
  tileHeight: number;
  imageFormat: string;
}

interface GoogleMapsSession {
  token: string;
  expiryTime: number;
  mapType: GoogleMapType;
}

const sessions = new Map<GoogleMapType, GoogleMapsSession>();
let apiKey: string | null = null;

export const setGoogleMapsApiKey = (key: string): void => {
  apiKey = key;
};

export const getGoogleMapsApiKey = (): string | null => apiKey;

const isSessionValid = (session: GoogleMapsSession): boolean => {
  return Date.now() < session.expiryTime - SESSION_REFRESH_BUFFER_MS;
};

export const createGoogleMapsSession = async (mapType: GoogleMapType): Promise<string | null> => {
  if (!apiKey) {
    return null;
  }

  const existingSession = sessions.get(mapType);
  if (existingSession && isSessionValid(existingSession)) {
    return existingSession.token;
  }

  const requestBody: CreateSessionRequest = {
    mapType,
    language: 'en-US',
    region: 'US',
  };

  try {
    const response = await axios.post<SessionResponse>(
      `${GOOGLE_TILES_API_BASE}/createSession?key=${apiKey}`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const session: GoogleMapsSession = {
      token: response.data.session,
      expiryTime: new Date(response.data.expiry).getTime(),
      mapType,
    };

    sessions.set(mapType, session);
    return session.token;
  } catch {
    return null;
  }
};

export const getGoogleMapsTileUrl = (mapType: GoogleMapType, sessionToken: string): string => {
  if (!apiKey) {
    throw new Error('Google Maps API key not set');
  }
  return `${GOOGLE_TILES_API_BASE}/2dtiles/{z}/{x}/{y}?session=${sessionToken}&key=${apiKey}`;
};

export const clearGoogleMapsSessions = (): void => {
  sessions.clear();
};

export const getAvailableGoogleMapTypes = (): { type: GoogleMapType; label: string }[] => [
  { type: 'roadmap', label: 'Google Roadmap' },
  { type: 'satellite', label: 'Google Satellite' },
];
